import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar, CreditCard, MessageSquare, AlertCircle } from 'lucide-react';
import Modal from '../../shared/components/Modal';
import Field from '../../shared/components/Field';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { sendTelegramNotification, formatDateFR } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';

export default function StaffPaymentForm({ employee, settings, onClose }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const { add } = useCollection('staff_payments');

  const [data, setData] = useState({
    type: 'salary',          // 'salary' | 'advance' | 'bonus' | 'penalty'
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    period: '',              // e.g. "Mai 2026" for monthly
    paymentMethod: 'cash',   // 'cash' | 'bank' | 'check'
    note: ''
  });

  const typeLabels = {
    salary:  { fr: 'Salaire',   ar: 'راتب',  emoji: '💰', color: 'green' },
    advance: { fr: 'Avance',    ar: 'سلفة',  emoji: '⏩', color: 'amber' },
    bonus:   { fr: 'Prime',     ar: 'علاوة', emoji: '🎁', color: 'blue' },
    penalty: { fr: 'Pénalité',  ar: 'خصم',   emoji: '⛔', color: 'red' }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!data.amount || parseFloat(data.amount) <= 0) {
      return alert(isAr ? '⚠️ المبلغ مطلوب' : '⚠️ Montant requis');
    }
    
    const payload = {
      employeeId: employee.id,
      employeeName: employee.name,
      employeeRole: employee.role,
      type: data.type,
      amount: parseFloat(data.amount),
      paymentDate: data.paymentDate,
      period: data.period || '',
      paymentMethod: data.paymentMethod,
      note: data.note || '',
      createdAt: new Date().toISOString()
    };
    
    try {
      await add(payload);
      
      // Telegram notification
      await sendTelegramNotification(
        buildTelegramMessage('staff_payment', {
          typeKey: data.type, name: employee.name, role: employee.role,
          amount: data.amount, currency: settings.currency || 'MAD',
          date: formatDateFR(data.paymentDate), period: data.period, note: data.note
        }),
        'staff_payment'
      );
      
      onClose();
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  // Auto-suggest period and amount
  useEffect(() => {
    if (data.type === 'salary' && employee.salaryType === 'monthly' && !data.period) {
      const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
      const d = new Date(data.paymentDate);
      setData(prev => ({ ...prev, period: `${months[d.getMonth()]} ${d.getFullYear()}` }));
    }
    if (data.type === 'salary' && employee.salaryAmount && !data.amount) {
      setData(prev => ({ ...prev, amount: employee.salaryAmount }));
    }
  }, [data.type, data.paymentDate, employee]);

  const t = (fr, ar) => (isAr ? ar : fr);

  return (
    <Modal onClose={onClose} title={t(`Enregistrer un paiement - ${employee.name}`, `تسجيل دفعة للموظف - ${employee.name}`)}>
      <form onSubmit={submit} className="space-y-5" dir={isAr ? 'rtl' : 'ltr'}>
        
        {/* Employee Summary Card */}
        <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-150 rounded-2xl flex items-center gap-3">
          {employee.photo ? (
            <img 
              src={employee.photo} 
              alt={employee.name} 
              className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-blue-600 text-white font-black flex items-center justify-center text-xl shadow-sm">
              {employee.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-bold text-gray-900 text-sm truncate">{employee.name}</div>
            <div className="text-[10px] text-gray-650 font-semibold">
              {employee.role} • {employee.salaryAmount} {settings.currency || 'MAD'}
              {employee.salaryType === 'monthly' ? t('/mois', '/شهريا') : t('/jour', '/يوميا')}
            </div>
          </div>
        </div>

        {/* Payment Type Selection */}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
            {t('Type de versement', 'نوع الدفعة')}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(typeLabels).map(([key, val]) => {
              const isActive = data.type === key;
              const activeColor = 
                key === 'salary' ? 'bg-green-50/50 border-green-500 text-green-950 shadow-sm ring-1 ring-green-100' :
                key === 'advance' ? 'bg-amber-50/50 border-amber-500 text-amber-950 shadow-sm ring-1 ring-amber-100' :
                key === 'bonus' ? 'bg-blue-50/50 border-blue-500 text-blue-950 shadow-sm ring-1 ring-blue-100' :
                'bg-red-50/50 border-red-500 text-red-950 shadow-sm ring-1 ring-red-100';
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setData({ ...data, type: key })}
                  className={`p-3 border-2 rounded-2xl flex flex-col items-center justify-center transition-all ${
                    isActive ? activeColor : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl mb-1">{val.emoji}</span>
                  <span className="text-xs font-bold">{t(val.fr, val.ar)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Amount + Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label={'💰 ' + t('Montant', 'المبلغ')}
            type="number"
            value={data.amount}
            onChange={v => setData({ ...data, amount: v })}
            placeholder="0.00"
          />
          <Field
            label={'📅 ' + t('Date de versement', 'تاريخ الصرف')}
            type="date"
            value={data.paymentDate}
            onChange={v => setData({ ...data, paymentDate: v })}
          />
        </div>

        {/* Period (Salary only) */}
        {data.type === 'salary' && (
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              {'📆 ' + t('Période concernée', 'الفترة المعنية')}
            </label>
            <input
              type="text"
              value={data.period}
              onChange={e => setData({ ...data, period: e.target.value })}
              placeholder={t('Ex: Mai 2026', 'مثلا: مايو 2026')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>
        )}

        {/* Mode de Paiement */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1">
            {'💳 ' + t('Mode de règlement', 'طريقة الدفع')}
          </label>
          <select
            value={data.paymentMethod}
            onChange={e => setData({ ...data, paymentMethod: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="cash">💵 {t('Espèces', 'نقداً')}</option>
            <option value="bank">🏦 {t('Virement bancaire', 'تحويل بنكي')}</option>
            <option value="check">📄 {t('Chèque', 'شيك')}</option>
          </select>
        </div>

        {/* Note */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1">
            {'📝 ' + t('Note / Commentaire', 'ملاحظة')}
          </label>
          <input
            type="text"
            value={data.note}
            onChange={e => setData({ ...data, note: e.target.value })}
            placeholder={t('Ex: Salaire du mois ou prime...', 'مثلا: راتب شهر مايو أو علاوة...')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold transition text-gray-700 text-sm"
          >
            {t('Annuler', 'إلغاء')}
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition text-sm"
          >
            💾 {t('Enregistrer le versement', 'حفظ الدفعة')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
